// frontend/src/pages/portfolios/PortfolioList.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import portfolioService from '../../services/portfolioService';
import { Portfolio } from '../../types/Portfolio';
import { 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  TableSortLabel
} from '@mui/material';
import { 
  Edit, 
  FileCopy, 
  Delete, 
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FiberManualRecord as DotIcon,
  RemoveRedEye as ViewIcon
} from '@mui/icons-material';

type SortField = 'title' | 'lastModified' | 'targetInvestor' | 'viewCount';
type SortOrder = 'asc' | 'desc';

const PortfolioList: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [sortField, setSortField] = useState<SortField>('lastModified');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const navigate = useNavigate();

  useEffect(() => {
    loadPortfolios();
  }, []);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      const data = await portfolioService.getAll();
      setPortfolios(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load portfolios');
      console.error('Error loading portfolios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const newPortfolio = await portfolioService.create({
        title: 'New Portfolio',
        investorContext: {
          fundingType: [],
          investorTags: [],
          interestAreas: []
        }
      });
      const portfolioId = (newPortfolio as any)._id || newPortfolio.id;
      navigate(`/admin/portfolios/${portfolioId}/edit`);
    } catch (err: any) {
      alert('Failed to create portfolio: ' + err.message);
    }
  };

  const handleClone = async (id: string) => {
    try {
      const cloned = await portfolioService.clone(id);
      await loadPortfolios();
      const portfolioId = (cloned as any)._id || cloned.id;
      navigate(`/admin/portfolios/${portfolioId}/edit`);
    } catch (err: any) {
      alert('Failed to clone portfolio: ' + err.message);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Delete portfolio "${title}"?`)) return;
    
    try {
      await portfolioService.delete(id);
      await loadPortfolios();
    } catch (err: any) {
      alert('Failed to delete portfolio: ' + err.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'default';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const getPortfolioId = (portfolio: any): string => {
    return portfolio._id || portfolio.id;
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'urgent': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return null;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Filter and sort portfolios
  const filteredAndSortedPortfolios = useMemo(() => {
    let filtered = portfolios;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(query) ||
        p.investorContext?.targetInvestor?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'targetInvestor':
          aVal = (a.investorContext?.targetInvestor || '').toLowerCase();
          bVal = (b.investorContext?.targetInvestor || '').toLowerCase();
          break;
        case 'viewCount':
          aVal = a.viewCount || 0;
          bVal = b.viewCount || 0;
          break;
        case 'lastModified':
          aVal = new Date(a.lastModified || a.createdAt).getTime();
          bVal = new Date(b.lastModified || b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [portfolios, searchQuery, statusFilter, sortField, sortOrder]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button onClick={loadPortfolios} variant="contained" sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Portfolios</Typography>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          Create New Portfolio
        </Button>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search by title or investor..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Status Filter Tabs */}
      <Tabs 
        value={statusFilter} 
        onChange={(_, newValue) => setStatusFilter(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All" value="all" />
        <Tab label="Published" value="published" />
        <Tab label="Draft" value="draft" />
      </Tabs>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'title'}
                  direction={sortField === 'title' ? sortOrder : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'targetInvestor'}
                  direction={sortField === 'targetInvestor' ? sortOrder : 'asc'}
                  onClick={() => handleSort('targetInvestor')}
                >
                  Target Investor
                </TableSortLabel>
              </TableCell>
              <TableCell>Funding Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'viewCount'}
                  direction={sortField === 'viewCount' ? sortOrder : 'asc'}
                  onClick={() => handleSort('viewCount')}
                >
                  Views
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'lastModified'}
                  direction={sortField === 'lastModified' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lastModified')}
                >
                  Last Modified
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedPortfolios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    {searchQuery || statusFilter !== 'all' 
                      ? 'No portfolios match your filters.' 
                      : 'No portfolios yet. Create your first one!'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedPortfolios.map((portfolio) => {
                const portfolioId = getPortfolioId(portfolio);
                const priority = portfolio.investorContext?.priority;
                const priorityColor = getPriorityColor(priority);
                const fundingTypes = portfolio.investorContext?.fundingType || [];
                const firstFundingType = fundingTypes[0];
                const additionalCount = fundingTypes.length - 1;
                const viewCount = portfolio.viewCount || 0;

                return (
                  <TableRow key={portfolioId} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {priorityColor && (
                          <DotIcon 
                            sx={{ 
                              fontSize: 12, 
                              color: priorityColor 
                            }} 
                          />
                        )}
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            cursor: 'pointer', 
                            '&:hover': { textDecoration: 'underline' } 
                          }}
                          onClick={() => navigate(`/admin/portfolios/${portfolioId}/edit`)}
                        >
                          {portfolio.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {portfolio.investorContext?.targetInvestor || '-'}
                    </TableCell>
                    <TableCell>
                      {firstFundingType ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip 
                            label={firstFundingType.replace(/_/g, ' ')} 
                            size="small"
                            variant="outlined"
                          />
                          {additionalCount > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              +{additionalCount}
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={portfolio.status} 
                        color={getStatusColor(portfolio.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ViewIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {viewCount}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(portfolio.lastModified || portfolio.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {/* View / Preview */}
                      {portfolio.status === 'published' ? (
                        <IconButton
                          size="small"
                          component="a"
                          href={`/portfolio/${portfolio.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View Live"
                        >
                          <VisibilityIcon color="success" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/portfolios/${portfolioId}/edit?preview=true`)}
                          title="Preview Draft"
                        >
                          <VisibilityIcon color="action" />
                        </IconButton>
                      )}

                      {/* Edit */}
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/portfolios/${portfolioId}/edit`)}
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>

                      {/* Clone */}
                      <IconButton
                        size="small"
                        onClick={() => handleClone(portfolioId)}
                        title="Clone"
                      >
                        <FileCopy />
                      </IconButton>

                      {/* Delete */}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(portfolioId, portfolio.title)}
                        title="Delete"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PortfolioList;